import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CartItemRow from "./components/CartItemRow";
import AddressForm from "./components/AddressForm";
import OrderConfirmation from "./components/OrderConfirmation";
import { mockCartItems, CartItem } from "@/mocks/cart";
import SEO from "@/components/base/SEO";

type Step = "cart" | "address" | "payment" | "confirmed";

interface AddressData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

const EMPTY_ADDRESS: AddressData = {
  firstName: "Jessica",
  lastName: "Martinez",
  email: "jessica@example.com",
  phone: "+1 (310) 555-0192",
  address: "4821 Sunset Blvd",
  apt: "Apt 12",
  city: "Los Angeles",
  state: "California",
  zip: "90028",
  country: "US",
};

const PAYMENT_METHODS = [
  { id: "card", label: "Credit / Debit Card", icon: "ri-bank-card-line" },
  { id: "paypal", label: "PayPal", icon: "ri-paypal-line" },
  { id: "apple", label: "Apple Pay", icon: "ri-apple-line" },
];

function generateOrderId() {
  return "SBP-" + Math.random().toString(36).substring(2, 8).toUpperCase();
}

export default function CartPage() {
  const [step, setStep] = useState<Step>("cart");
  const [items, setItems] = useState<CartItem[]>(mockCartItems);
  const [address, setAddress] = useState<AddressData>(EMPTY_ADDRESS);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cardNumber, setCardNumber] = useState("4242 4242 4242 4242");
  const [cardExpiry, setCardExpiry] = useState("12/28");
  const [cardCvc, setCardCvc] = useState("123");
  const [cardName, setCardName] = useState("Jessica Martinez");
  const [orderId] = useState(generateOrderId());
  const [addressErrors, setAddressErrors] = useState<Partial<AddressData>>({});
  const [isPlacing, setIsPlacing] = useState(false);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const shipping = 0;
  const total = subtotal + shipping;

  const handleQuantityChange = (id: string, qty: number) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, quantity: qty } : i));
  };

  const handleRemove = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const validateAddress = () => {
    const errors: Partial<AddressData> = {};
    if (!address.firstName.trim()) errors.firstName = "Required";
    if (!address.lastName.trim()) errors.lastName = "Required";
    if (!address.email.trim()) errors.email = "Required";
    if (!address.phone.trim()) errors.phone = "Required";
    if (!address.address.trim()) errors.address = "Required";
    if (!address.city.trim()) errors.city = "Required";
    if (!address.state) errors.state = "Required";
    if (!address.zip.trim()) errors.zip = "Required";
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddressNext = () => {
    if (validateAddress()) setStep("payment");
  };

  const handlePlaceOrder = () => {
    setIsPlacing(true);
    setTimeout(() => {
      setIsPlacing(false);
      setStep("confirmed");
    }, 1800);
  };

  const steps: { key: Step; label: string }[] = [
    { key: "cart", label: "Cart" },
    { key: "address", label: "Shipping" },
    { key: "payment", label: "Payment" },
    { key: "confirmed", label: "Confirmed" },
  ];

  const stepIndex = steps.findIndex((s) => s.key === step);

  if (step === "confirmed") {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Order Confirmed — StarBookPrime" description="Your order has been placed." canonical="/cart" noIndex={true} />
        <Navbar />
        <div className="pt-16">
          <OrderConfirmation items={items} total={total} orderId={orderId} address={address} />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f5f5" }}>
      <SEO title="Shopping Cart — StarBookPrime" description="Review your cart and complete your purchase." canonical="/cart" noIndex={true} />
      <Navbar />
      <div className="pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-gray-900 mb-4">
              {step === "cart" ? "Shopping Cart" : step === "address" ? "Shipping Address" : "Payment"}
            </h1>
            {/* Step Indicator */}
            <div className="flex items-center gap-0">
              {steps.slice(0, 3).map((s, i) => (
                <div key={s.key} className="flex items-center">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${i <= stepIndex ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-400"}`}>
                    <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${i < stepIndex ? "bg-white/30" : ""}`}>
                      {i < stepIndex ? <i className="ri-check-line" /> : i + 1}
                    </span>
                    {s.label}
                  </div>
                  {i < 2 && <div className={`w-8 h-0.5 ${i < stepIndex ? "bg-orange-400" : "bg-gray-200"}`} />}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              {/* STEP 1: Cart */}
              {step === "cart" && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-bold text-gray-900">{items.length} Items</h2>
                    <Link to="/marketplace" className="text-xs text-orange-500 hover:underline cursor-pointer">+ Add more items</Link>
                  </div>
                  {items.length === 0 ? (
                    <div className="py-16 text-center">
                      <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-4">
                        <i className="ri-shopping-cart-line text-3xl text-gray-300" />
                      </div>
                      <p className="text-gray-500 font-medium">Your cart is empty</p>
                      <Link to="/marketplace" className="inline-block mt-4 px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                        Browse Products
                      </Link>
                    </div>
                  ) : (
                    <>
                      {items.map((item) => (
                        <CartItemRow key={item.id} item={item} onQuantityChange={handleQuantityChange} onRemove={handleRemove} />
                      ))}
                      <div className="mt-6 flex justify-end">
                        <button
                          onClick={() => setStep("address")}
                          className="px-8 py-3 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                        >
                          Proceed to Shipping <i className="ri-arrow-right-line" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* STEP 2: Address */}
              {step === "address" && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h2 className="text-base font-bold text-gray-900 mb-5">Delivery Information</h2>
                  <AddressForm data={address} onChange={(field, value) => {
                    setAddress((prev) => ({ ...prev, [field]: value }));
                    setAddressErrors((prev) => ({ ...prev, [field]: undefined }));
                  }} />
                  {Object.keys(addressErrors).length > 0 && (
                    <p className="text-xs text-red-500 mt-3">Please fill in all required fields.</p>
                  )}
                  <div className="flex gap-3 mt-6">
                    <button onClick={() => setStep("cart")} className="px-6 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                      Back
                    </button>
                    <button onClick={handleAddressNext} className="flex-1 py-3 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                      Continue to Payment <i className="ri-arrow-right-line" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Payment */}
              {step === "payment" && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h2 className="text-base font-bold text-gray-900 mb-5">Payment Method</h2>
                  <div className="flex flex-col gap-3 mb-6">
                    {PAYMENT_METHODS.map((pm) => (
                      <button
                        key={pm.id}
                        onClick={() => setPaymentMethod(pm.id)}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-colors cursor-pointer text-left ${paymentMethod === pm.id ? "border-orange-400 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === pm.id ? "border-orange-500" : "border-gray-300"}`}>
                          {paymentMethod === pm.id && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                        </div>
                        <i className={`${pm.icon} text-lg ${paymentMethod === pm.id ? "text-orange-500" : "text-gray-400"}`} />
                        <span className={`text-sm font-semibold ${paymentMethod === pm.id ? "text-gray-900" : "text-gray-600"}`}>{pm.label}</span>
                      </button>
                    ))}
                  </div>

                  {paymentMethod === "card" && (
                    <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-xl">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Card Number</label>
                        <input type="text" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder="1234 5678 9012 3456" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Cardholder Name</label>
                        <input type="text" value={cardName} onChange={(e) => setCardName(e.target.value)} placeholder="John Doe" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white transition-colors" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Expiry Date</label>
                          <input type="text" value={cardExpiry} onChange={(e) => setCardExpiry(e.target.value)} placeholder="MM/YY" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white transition-colors" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">CVC</label>
                          <input type="text" value={cardCvc} onChange={(e) => setCardCvc(e.target.value)} placeholder="123" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white transition-colors" />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === "paypal" && (
                    <div className="mb-6 p-6 bg-gray-50 rounded-xl text-center">
                      <i className="ri-paypal-line text-4xl text-gray-400 mb-2 block" />
                      <p className="text-sm text-gray-500">You&apos;ll be redirected to PayPal to complete your payment securely.</p>
                    </div>
                  )}

                  {paymentMethod === "apple" && (
                    <div className="mb-6 p-6 bg-gray-50 rounded-xl text-center">
                      <i className="ri-apple-line text-4xl text-gray-400 mb-2 block" />
                      <p className="text-sm text-gray-500">Use Face ID or Touch ID to pay with Apple Pay.</p>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
                    <i className="ri-shield-check-line text-green-500" />
                    <span>Your payment is secured with 256-bit SSL encryption</span>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => setStep("address")} className="px-6 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                      Back
                    </button>
                    <button
                      onClick={handlePlaceOrder}
                      disabled={isPlacing}
                      className="flex-1 py-3 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {isPlacing ? (
                        <><i className="ri-loader-4-line animate-spin" /> Processing...</>
                      ) : (
                        <><i className="ri-lock-line" /> Place Order — ${total.toFixed(2)}</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Order Summary</h3>
                <div className="space-y-3 mb-4">
                  {items.map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-50">
                          <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top" />
                        </div>
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full">{item.quantity}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 line-clamp-2">{item.title}</p>
                        <p className="text-xs text-gray-400">{item.seller}</p>
                      </div>
                      <span className="text-xs font-semibold text-gray-900 flex-shrink-0">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Subtotal ({items.reduce((s, i) => s + i.quantity, 0)} items)</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Shipping</span>
                    <span className="text-green-600 font-semibold">Free</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Tax</span>
                    <span>Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-gray-100">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Promo Code */}
                <div className="mt-4 flex gap-2">
                  <input type="text" placeholder="Promo code" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-orange-400 transition-colors" />
                  <button className="px-3 py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">Apply</button>
                </div>

                {/* Trust Badges */}
                <div className="mt-5 pt-4 border-t border-gray-100 space-y-2">
                  {[
                    { icon: "ri-shield-check-line", text: "Buyer Protection Guarantee", color: "text-green-500" },
                    { icon: "ri-truck-line", text: "Free Shipping on All Orders", color: "text-orange-500" },
                    { icon: "ri-refresh-line", text: "30-Day Easy Returns", color: "text-orange-500" },
                  ].map((b) => (
                    <div key={b.text} className="flex items-center gap-2 text-xs text-gray-500">
                      <i className={`${b.icon} ${b.color}`} />
                      {b.text}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
